import React, { useEffect, useRef } from 'react';

export type FaceLayoutEntry = {
  userId: string;
  vx: number;
  vy: number;
  vz: number;
  visible: boolean;
};

export type FacePinParticipant = {
  id: string;
  userId?: string;
  intersectId?: string;
  identityKeys?: string[];
};

type Props = {
  participantStreams: Map<string, MediaStream>;
  participants: FacePinParticipant[];
  localUserId?: string;
  localStream?: MediaStream | null;
};

const normalizeId = (value: unknown): string => String(value ?? '').trim().toLowerCase();
export const MAX_IVS_REALTIME_PUBLISHERS = 12;

export function getFacePinCapacity(): number {
  return MAX_IVS_REALTIME_PUBLISHERS;
}

export function selectVisibleFaces(faces: FaceLayoutEntry[], limit = getFacePinCapacity()): FaceLayoutEntry[] {
  return faces
    .filter((face) => {
      return (
        !!normalizeId(face?.userId) &&
        face.visible === true &&
        Number.isFinite(face.vx) &&
        Number.isFinite(face.vy) &&
        Number.isFinite(face.vz) &&
        face.vz > 0
      );
    })
    .sort((a, b) => a.vz - b.vz)
    .slice(0, Math.max(0, limit));
}

export function participantIdentityKeys(participant: any): string[] {
  const values = [
    participant?.userId,
    participant?.info?.userId,
    participant?.intersectId,
    participant?.attributes?.intersectId,
    participant?.info?.attributes?.intersectId,
    participant?.attributes?.username,
    participant?.info?.attributes?.username,
    ...(Array.isArray(participant?.identityKeys) ? participant.identityKeys : []),
  ];
  return Array.from(new Set(values.map(normalizeId).filter(Boolean)));
}


type VideoSource = {
  video: HTMLVideoElement;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
};
type BridgeWindow = Window & { __vvFaceVideoSources?: Map<string, VideoSource> };

/** IVS owns the tracks. Unity pulls small frames from these shared sources. */
const AvatarFacePins: React.FC<Props> = ({ participantStreams, participants, localUserId, localStream }) => {
  const owned = useRef(new Map<MediaStream, VideoSource>());
  const registry = useRef(new Map<string, VideoSource>());
  useEffect(() => {
    const wanted = new Map<MediaStream, string[]>();
    for (const participant of participants) {
      const stream = participantStreams.get(participant.id);
      if (stream && stream.getVideoTracks().some(track => track.readyState === 'live')) {
        wanted.set(stream, [...(wanted.get(stream) || []), ...participantIdentityKeys(participant)]);
      }
    }
    if (localStream && normalizeId(localUserId) && localStream.getVideoTracks().some(t => t.readyState === 'live')) {
      wanted.set(localStream, [...(wanted.get(localStream) || []), normalizeId(localUserId)]);
    }
    for (const [stream, source] of Array.from(owned.current.entries())) {
      if (wanted.has(stream)) continue;
      source.video.pause();
      source.video.srcObject = null;
      source.canvas.width = source.canvas.height = 1;
      owned.current.delete(stream);
    }
    registry.current.clear();
    for (const [stream, keys] of Array.from(wanted.entries())) {
      let source = owned.current.get(stream);
      if (!source) {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = 128;
        const context = canvas.getContext('2d', { alpha: false });
        if (!context) continue;
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.autoplay = true;
        video.srcObject = stream;
        void video.play().catch(() => {});
        source = { video, canvas, context };
        owned.current.set(stream, source);
      }
      keys.forEach(key => registry.current.set(key, source!));
    }
    (window as BridgeWindow).__vvFaceVideoSources = registry.current;
  }, [participantStreams, participants, localUserId, localStream]);

  useEffect(() => {
    const sources = owned.current;
    const entries = registry.current;
    return () => {
      const bridge = window as BridgeWindow;
      if (bridge.__vvFaceVideoSources === entries) delete bridge.__vvFaceVideoSources;
      for (const source of Array.from(sources.values())) {
        source.video.pause();
        source.video.srcObject = null;
        source.canvas.width = source.canvas.height = 1;
      }
      sources.clear();
      entries.clear();
    };
  }, []);
  return null;
};
export default React.memo(AvatarFacePins);
